'use client'

import { useEffect, useState } from 'react'

import fetchWithTimeout from '@/lib/utils/fetch-with-timeout'

import { generateImageKitURL } from '@/lib/generate-imagekit-url'

import { useRive, decodeImage, RuntimeLoader } from '@rive-app/react-canvas-lite'

// Use self-hosted Rive WASM file instead of CDN (Update this if/when Rive is updated)
const riveWASMResource = '/rive/rive.wasm'
RuntimeLoader.setWasmUrl(riveWASMResource)

export default function HeroRiveAnimation({
  animationName,
  showAsset = true,
  isActive = true,
}: {
  animationName: string
  isActive?: boolean
  showAsset?: boolean
}) {
  const [fileExists, setFileExists] = useState(true)

  const customAssetLoader = (asset, bytes) => {
    if (showAsset && asset.isImage && asset.name === 'image') {
      // Generate an image URL matching the asset we're replacing
      const imageUrl = generateImageKitURL(`/rive/${animationName}.webp?v=2`, {
        width: '1000',
        format: 'webp',
        height: '1000',
        pr: 'true',
        quality: '70',
        cropMode: 'pad_resize',
        focus: 'bottom_right',
      })

      // Replace the asset and remove previous image reference
      fetchWithTimeout(imageUrl)
        .then(async (res) => {
          if (!res.ok) {
            // Bail out if the image is not found
            // console.log('Image not found:', imageUrl)
            return
          }
          const image = await decodeImage(new Uint8Array(await res.arrayBuffer()))
          asset.setRenderImage(image)
          image.unref()
        })
        .catch((error) => {
          console.error('Error loading image:', error)
        })

      // Report back that we are handling the asset
      return true
    }

    return false
  }

  const { rive, RiveComponent } = useRive({
    src: `/rive/${animationName}.riv`,
    artboard: 'Master',
    stateMachines: 'state',
    autoplay: true,
    // Fetch and serve replacement image
    assetLoader: customAssetLoader,
  })

  // Pause and play based on isActive prop
  useEffect(() => {
    if (rive) {
      if (isActive) {
        rive.play()
      } else {
        rive.pause()
      }
    }
  }, [rive, isActive])

  // Check if the file exists in the public directory
  useEffect(() => {
    const checkFileExists = async () => {
      try {
        const response = await fetchWithTimeout(`/rive/${animationName}.riv`)

        if (!response.ok) {
          setFileExists(false)
        }
      } catch (error) {
        console.error('Error checking file existence:', error)
      }
    }

    checkFileExists()
  }, [animationName])

  if (!fileExists) {
    return null
  }

  return (
    <>
      <link rel="preload" href={riveWASMResource} as="fetch" crossOrigin="anonymous" />
      <RiveComponent />
    </>
  )
}
